/**
 * Public form fill page — /p/:slug/:formCode
 * Anonymous, no auth required. Validates with a server-issued math captcha.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { DynamicFormRenderer } from "@/components/app/DynamicFormRenderer";
import { CheckCircle, ArrowLeft, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function PublicFormFill() {
    const { slug, formCode } = useParams();
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);

    const [captcha, setCaptcha] = useState(null);
    const [captchaAnswer, setCaptchaAnswer] = useState("");

    // PIN state
    const [pinStatus, setPinStatus] = useState(null);   // { pin_required, form_name }
    const [pinDraft, setPinDraft] = useState("");
    const [pin, setPin] = useState("");                  // verified pin used on submit

    const loadCaptcha = async () => {
        try {
            const { data } = await axios.get(`${BASE}/api/public/forms/${slug}/${formCode}/captcha`);
            setCaptcha(data); setCaptchaAnswer("");
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not load captcha"); }
    };

    const loadForm = async (pinValue) => {
        try {
            const url = `${BASE}/api/public/forms/${slug}/${formCode}${pinValue ? `?pin=${encodeURIComponent(pinValue)}` : ""}`;
            const { data } = await axios.get(url);
            setMeta(data);
            const seed = {};
            (data.fields || []).forEach((f) => {
                if (f.default_value != null) seed[f.field_name] = f.default_value;
            });
            setData(seed);
            await loadCaptcha();
            return true;
        } catch (e) {
            if (e?.response?.status === 403) {
                toast.error("Invalid PIN");
                return false;
            }
            setError(e?.response?.data?.detail || "Form not found or not public");
            return false;
        }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data: status } = await axios.get(`${BASE}/api/public/forms/${slug}/${formCode}/pin-status`);
                if (cancelled) return;
                setPinStatus(status);
                if (!status.pin_required) {
                    await loadForm(null);
                }
            } catch (e) {
                if (!cancelled) setError(e?.response?.data?.detail || "Form not found or not public");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line
    }, [slug, formCode]);

    const submitPin = async (e) => {
        e?.preventDefault();
        const v = pinDraft.trim();
        if (!v) { toast.error("Enter the PIN"); return; }
        setLoading(true);
        const ok = await loadForm(v);
        if (ok) setPin(v);
        setLoading(false);
    };

    const submit = async (e) => {
        e?.preventDefault();
        if (!captcha) { toast.error("Captcha not loaded"); return; }
        const answer = Number(captchaAnswer);
        if (!Number.isFinite(answer)) { toast.error("Please solve the captcha"); return; }
        setSubmitting(true);
        try {
            await axios.post(`${BASE}/api/public/forms/${slug}/${formCode}`, {
                data,
                captcha_token: captcha.captcha_token,
                captcha_answer: answer,
                pin: pin || undefined,
            });
            setSubmitted(true);
            toast.success("Submitted");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Submission failed");
            // refresh captcha on any failure (most failures invalidate it)
            await loadCaptcha();
        } finally { setSubmitting(false); }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Loading form…</div>
            </div>
        );
    }
    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6 auth-grid-bg">
                <div className="brut-section p-12 max-w-md text-center" data-testid="public-form-error">
                    <div className="label-overline mb-2">404</div>
                    <h1 className="text-2xl font-bold tracking-tight mb-2">Form not available</h1>
                    <p className="text-sm text-muted-foreground mb-6">{error}</p>
                    <Link to={`/p/${slug}`}><Button variant="outline" className="rounded-sm">Back to {slug}</Button></Link>
                </div>
            </div>
        );
    }
    // PIN gate (when required and not yet provided)
    if (pinStatus?.pin_required && !meta) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6 auth-grid-bg">
                <form onSubmit={submitPin} className="brut-section p-10 max-w-md w-full" data-testid="public-pin-gate">
                    <div className="label-overline mb-2">Access Code</div>
                    <h1 className="text-2xl font-bold tracking-tight mb-2">{pinStatus.form_name}</h1>
                    <p className="text-sm text-muted-foreground mb-6">This form is protected. Enter the PIN shared by the form owner.</p>
                    <Label className="label-overline">PIN</Label>
                    <Input
                        type="text"
                        value={pinDraft}
                        onChange={(e) => setPinDraft(e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 12))}
                        placeholder="••••"
                        autoFocus
                        className="mt-2 rounded-sm h-12 text-center font-mono text-lg tracking-[0.4em]"
                        data-testid="public-pin-input"
                    />
                    <Button type="submit" className="w-full mt-4 rounded-sm h-11 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="public-pin-submit">
                        Unlock form
                    </Button>
                </form>
            </div>
        );
    }
    if (submitted) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6 auth-grid-bg">
                <div className="brut-section p-12 max-w-md text-center" data-testid="public-submit-success">
                    <CheckCircle size={32} weight="duotone" className="mx-auto mb-4 text-primary" />
                    <div className="label-overline mb-2">Received</div>
                    <h1 className="text-2xl font-bold tracking-tight mb-2">Thanks — we got it.</h1>
                    <p className="text-sm text-muted-foreground mb-6">Your response was sent to {meta?.org?.name}.</p>
                    <div className="flex gap-2 justify-center">
                        <Button
                            onClick={async () => { setSubmitted(false); setData({}); await loadCaptcha(); }}
                            className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground"
                            data-testid="public-submit-another-btn"
                        >Submit another</Button>
                        <Link to={`/p/${slug}`}><Button variant="outline" className="rounded-sm">Back</Button></Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-secondary/30" data-testid="public-form-fill-page">
            <div className="bg-card border-b border-border">
                <div className="max-w-3xl mx-auto px-6 py-6">
                    <Link to={`/p/${slug}`} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-3" data-testid="public-back-link">
                        <ArrowLeft size={12} /> {meta?.org?.name}
                    </Link>
                    <div className="flex items-center gap-2 mb-2">
                        <div className="label-overline">{meta?.form?.form_code}</div>
                        <Badge variant="outline" className="rounded-sm font-mono text-[10px]">public</Badge>
                    </div>
                    <h1 className="text-3xl font-bold tracking-tight" data-testid="public-form-title">{meta?.form?.form_title || meta?.form?.form_name}</h1>
                    {meta?.form?.description && <p className="text-sm text-muted-foreground mt-2">{meta.form.description}</p>}
                </div>
            </div>
            <div className="max-w-3xl mx-auto p-6">
                <form onSubmit={submit} className="brut-section p-8 bg-card" data-testid="public-form">
                    <DynamicFormRenderer
                        sections={meta.sections} fields={meta.fields}
                        data={data} onChange={setData}
                        layoutMode={meta?.form?.layout_type || meta?.form?.layout_mode || "single"}
                    />

                    <div className="border-t border-border pt-6 mt-8" data-testid="public-captcha-section">
                        <Label className="label-overline mb-3 block">Quick math check</Label>
                        <div className="flex items-center gap-3 flex-wrap">
                            <div className="border border-border rounded-sm px-4 py-2 bg-secondary font-mono text-base" data-testid="captcha-challenge">
                                {captcha ? `${captcha.a} ${captcha.op} ${captcha.b} = ?` : "Loading…"}
                            </div>
                            <Input
                                type="number" required value={captchaAnswer}
                                onChange={(e) => setCaptchaAnswer(e.target.value)}
                                placeholder="?" className="rounded-sm w-24"
                                data-testid="captcha-answer-input"
                            />
                            <Button
                                type="button" variant="ghost" size="icon" className="rounded-sm"
                                onClick={loadCaptcha} title="New challenge"
                                data-testid="captcha-refresh-btn"
                            ><ArrowsClockwise size={14} /></Button>
                        </div>
                    </div>

                    <div className="pt-6 mt-6 flex items-center justify-end border-t border-border">
                        <Button
                            type="submit" disabled={submitting}
                            className="rounded-sm h-11 px-8 bg-primary hover:bg-primary/90 text-primary-foreground"
                            data-testid="public-submit-btn"
                        >
                            {submitting ? "Submitting…" : "Submit"}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
